import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, takeLatest, all } from "redux-saga/effects";
import {
  fetchDashboard,
  fetchDashboardStart,
  fetchDashboardSuccess,
  fetchDashboardFailure,
  DashboardRangePayload,
} from "../slices/dashboardSlice";
import api from "../../utils/api";

function* fetchDashboardSaga(
  action: PayloadAction<DashboardRangePayload | undefined>,
): Generator<any, void, any> {
  try {
    yield put(fetchDashboardStart());
    // Date range only narrows the time-based endpoints; all-time widgets
    // (top products, batch status, conversion ratio) and the global stats
    // ignore the range so the dashboard's "lifetime" cards stay stable.
    const range = action.payload;
    const rangeQs = range ? `start_date=${range.startDate}&end_date=${range.endDate}` : "";
    const withRange = (path: string) => {
      if (!rangeQs) return path;
      const sep = path.includes("?") ? "&" : "?";
      return `${path}${sep}${rangeQs}`;
    };

    const [
      statsRes,
      kpisRes,
      scanTrendRes,
      recentRes,
      topProductsRes,
      batchStatusRes,
      conversionRatioRes,
      timeSeriesRes,
    ] = yield all([
      call(api.get, "/analytics/dashboard"),
      call(api.get, withRange("/analytics/dashboard/kpis")),
      call(api.get, withRange("/analytics/dashboard/scan-activity-trend")),
      call(api.get, withRange("/analytics/dashboard/recent-activity?limit=10")),
      call(api.get, "/analytics/dashboard/top-products-by-scans?limit=5"),
      call(api.get, "/analytics/dashboard/batch-status-distribution"),
      call(api.get, "/analytics/dashboard/conversion-ratio"),
      call(api.get, withRange("/analytics/dashboard/time-series")),
    ]);

    yield put(
      fetchDashboardSuccess({
        stats: statsRes.data,
        kpis: kpisRes.data,
        scanTrend: scanTrendRes.data,
        recentActivity: recentRes.data,
        topProducts: topProductsRes.data,
        batchStatus: batchStatusRes.data,
        conversionRatio: conversionRatioRes.data,
        timeSeries: timeSeriesRes.data,
      }),
    );
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "Failed to fetch dashboard data";
    yield put(fetchDashboardFailure(errorMessage));
  }
}

export function* dashboardSaga() {
  yield takeLatest(fetchDashboard.type, fetchDashboardSaga);
}
